// pages/chart/modular/UnknownPanel.tsx — fallback card for unbuilt/removed panel types (§6.1)
//
// Renders for any PanelType whose wrapper is not yet wired (Phase 1: everything except
// 'price-chart') and for the 'unknown' forward-compat type (a persisted layout that
// references a removed type). Never crashes the grid; offers a dismiss that removes the
// panel from the active layout. The frame chrome (drag handle / close) stays interactive
// because PanelFrame renders this inside the panel body, not the header.

import { useDashboardStore } from '../../../stores/dashboardStore';
import { getPanelDef } from './panelRegistry';

interface UnknownPanelProps {
  panelId: string;
}

/** Read this panel's type label for a friendlier message; falls back gracefully. */
function usePanelTypeLabel(panelId: string): string {
  return useDashboardStore((s) => {
    const layout = s.layouts[s.activeLayout];
    const cfg = layout?.panelConfigs[panelId];
    if (!cfg) return 'panel';
    return getPanelDef(cfg.type)?.label ?? cfg.type;
  });
}

export function UnknownPanel({ panelId }: UnknownPanelProps) {
  const label = usePanelTypeLabel(panelId);
  const activeLayout = useDashboardStore((s) => s.activeLayout);
  const removePanel = useDashboardStore((s) => s.removePanel);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
      <p className="text-xs uppercase tracking-[0.2em] text-text-muted">
        Panel unavailable
      </p>
      <p className="text-sm text-text-secondary">
        <span className="font-medium text-text-primary">{label}</span> is not
        available yet.
      </p>
      <button
        type="button"
        onClick={() => removePanel(activeLayout, panelId)}
        className="no-drag rounded border border-border px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-bg-hover"
      >
        Dismiss
      </button>
    </div>
  );
}

export default UnknownPanel;
